import React, { useEffect, useState } from "react";
import { useWindowSize } from "../componets/useWindowSize";
import {
  getButtonCollapsedMemory,
  getMenuCollapsedMemory,
  getVersionApp,
  setButtonCollapsedmemory,
  setMenuCollapsedmemory,
} from "../data/memory";
import { getDatabase, onValue, ref } from "firebase/database";
import { getAuth } from "firebase/auth";
import { autoResetJogosMensal } from "../data/autoClear";
import { resetList } from "../data/resetList";
import { saveAppVersion } from "../data/userService";
import AppShell from "../componets/AppShell";

interface UserData {
  uid: string;
  displayName: string;
  jogosAnual: number;
  inscricao: number;
}

const Estatistica: React.FC = () => {
  const { width } = useWindowSize();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(
    getMenuCollapsedMemory()
  );
  const [isButtonCollapsed, setIsButtonCollapsed] = useState(
    getButtonCollapsedMemory()
  );
  const [ranking, setRanking] = useState<UserData[]>([]);
  const [currentUserUid, setCurrentUserUid] = useState<string | null>(null);
  const anoAtual = new Date().getFullYear();

  useEffect(() => {
    resetList();
    saveAppVersion();
    autoResetJogosMensal();
  }, []);

  useEffect(() => {
    if (width < 1000) {
      setIsMenuCollapsed(true);
      setMenuCollapsedmemory(true);
    } else if (!getButtonCollapsedMemory()) {
      setIsMenuCollapsed(false);
      setMenuCollapsedmemory(false);
    }
  }, [width]);

  const handleMenuToggle = () => {
    const newButtonCollapsed = !isButtonCollapsed;
    const newMenuCollapsed = !isMenuCollapsed;
    setIsButtonCollapsed(newButtonCollapsed);
    setButtonCollapsedmemory(newButtonCollapsed);
    setIsMenuCollapsed(newMenuCollapsed);
    setMenuCollapsedmemory(newMenuCollapsed);
  };

  const [fullRanking, setFullRanking] = useState<UserData[]>([]);
  const [userPosition, setUserPosition] = useState<number | null>(null);

  useEffect(() => {
    const fetchRanking = async () => {
      const db = getDatabase();
      const usersRef = ref(db, "users");
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        setCurrentUserUid(user.uid);
      }

      onValue(usersRef, (snapshot) => {
        const data = snapshot.val();
        if (!data) return;

        const fullList: UserData[] = Object.entries(data)
          .filter(([, user]: [string, any]) => user?.bloq !== true)
          .map(([uid, user]: [string, any]) => ({
            uid,
            displayName: user.displayName || "Sem nome",
            jogosAnual: user.jogosAnual || 0,
            inscricao: user.inscricao ? new Date(user.inscricao).getTime() : 0,
          }))
          .filter((user) => user.jogosAnual > 0)
          .sort((a, b) => {
            if (b.jogosAnual !== a.jogosAnual) {
              return b.jogosAnual - a.jogosAnual;
            }

            // Desempate por horário da inscrição (sem considerar a data)
            const aDate = new Date(a.inscricao);
            const bDate = new Date(b.inscricao);

            const aTime =
              aDate.getHours() * 3600 * 1000 +
              aDate.getMinutes() * 60 * 1000 +
              aDate.getSeconds() * 1000 +
              aDate.getMilliseconds();

            const bTime =
              bDate.getHours() * 3600 * 1000 +
              bDate.getMinutes() * 60 * 1000 +
              bDate.getSeconds() * 1000 +
              bDate.getMilliseconds();

            return aTime - bTime;
          });

        setFullRanking(fullList);

        const top10 = fullList.slice(0, 10);
        setRanking(top10);

        if (user) {
          const pos = fullList.findIndex((u) => u.uid === user.uid);
          if (pos >= 10) {
            setUserPosition(pos + 1);
          } else {
            setUserPosition(null); // Está no top 10
          }
        }
      });
    };

    fetchRanking();
  }, []);

  const getMedalEmoji = (index: number) => {
    return index === 0
      ? "🥇"
      : index === 1
      ? "🥈"
      : index === 2
      ? "🥉"
      : index + 1;
  };

  return (
    <AppShell
      isMenuCollapsed={isMenuCollapsed}
      onToggleMenu={handleMenuToggle}
    >
      <section className="panel-glass rounded-[2rem] p-4 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-center sm:text-left">
            Viciômetro
          </h2>

          {/* Top 3 em destaque */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 text-center">
            {ranking.slice(0, 3).map((user, index) => (
              <div
                key={user.uid}
                className="panel-soft rounded-[1.5rem] px-4 py-6"
              >
                <div className="text-3xl">{getMedalEmoji(index)}</div>
                <div className="mt-2 text-lg font-semibold">
                  {user.displayName}
                </div>
                <div className="text-sm text-zinc-300">
                  {user.jogosAnual} {user.jogosAnual === 1 ? "jogo" : "jogos"}{" "}
                  em {anoAtual}
                </div>
              </div>
            ))}
          </div>

          {/* Tabela com restante do top 10 */}
          <div className="panel-soft custom-scrollbar overflow-x-auto rounded-[1.5rem]">
            <table className="min-w-full table-auto text-left text-white text-sm sm:text-base">
              <thead className="border-b border-white/10 text-zinc-300">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Jogos em {anoAtual}</th>
                </tr>
              </thead>
              <tbody>
                {ranking.slice(3).map((user, index) => {
                  const isCurrentUser = user.uid === currentUserUid;
                  return (
                    <tr
                      key={user.uid}
                      className="border-b border-white/8"
                    >
                      <td
                        className={`px-4 py-2 ${
                          isCurrentUser ? "text-rose-300 font-semibold" : ""
                        }`}
                      >
                        {index + 4}
                      </td>
                      <td
                        className={`px-4 py-2 ${
                          isCurrentUser ? "text-rose-300 font-semibold" : ""
                        }`}
                      >
                        {user.displayName}
                      </td>
                      <td
                        className={`px-4 py-2 ${
                          isCurrentUser ? "text-rose-300 font-semibold" : ""
                        }`}
                      >
                        {user.jogosAnual}
                      </td>
                    </tr>
                  );
                })}

                {/* Usuário fora do top 10 */}
                {userPosition &&
                  currentUserUid &&
                  (() => {
                    const user = fullRanking.find(
                      (u) => u.uid === currentUserUid
                    );
                    if (!user) return null;

                    return (
                      <tr>
                        <td className="px-4 py-2 text-rose-300 font-semibold">
                          {userPosition}
                        </td>
                        <td className="px-4 py-2 text-rose-300 font-semibold">
                          {user.displayName}
                        </td>
                        <td className="px-4 py-2 text-rose-300 font-semibold">
                          {user.jogosAnual}
                        </td>
                      </tr>
                    );
                  })()}
              </tbody>
            </table>
          </div>
      </section>

      <div className="w-full py-2 text-center">
          <span className="text-xs text-zinc-500 sm:text-sm">
            Developed by Samuel Stork
          </span>{" "}
          <span className="text-xs text-zinc-600 sm:text-sm">{getVersionApp()}</span>
        </div>
    </AppShell>
  );
};

export default Estatistica;
